import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { flattenPages, nextCursorParam } from '@/hooks/queries/pagination';
import { getBrokerBatchTransactions, getBrokerTransactionBatch, type PaginatedResponse, type WireBatchTransaction } from '@/services/api/brokers';

/**
 * Definitive 404 (`TRANSACTION_BATCH_NOT_FOUND`) — the batch page swaps to its
 * not-found state instead of a half-empty page. The query client already
 * skips retries on 404, so no per-query retry override.
 *
 * @deprecated Import isNotFoundError from '@/lib/api/error'. This
 * re-export only remains for broker-detail.tsx until its wave repoints the import.
 */
export { isNotFoundError } from '@/lib/api/error';

export const brokerBatchQueryKey = (brokerId: string, batchId: string) => ['brokers', brokerId, 'batches', batchId] as const;
export const brokerBatchTransactionsQueryKey = (brokerId: string, batchId: string) => [...brokerBatchQueryKey(brokerId, batchId), 'transactions'] as const;

// Stable module-level select via flattenPages (pagination.ts) — the
// select-memoization rationale and the malformed-page `?? []` guard live there.
const selectBatchTransactions = flattenPages((page: PaginatedResponse<WireBatchTransaction>) => page.items);

export const brokerBatchQueryOptions = (brokerId: string, batchId: string) =>
  queryOptions({
    queryKey: brokerBatchQueryKey(brokerId, batchId),
    // The service unwraps the `{ batch }` envelope — unique to this endpoint —
    // so consumers see WireTransactionBatch directly.
    queryFn: () => getBrokerTransactionBatch(brokerId, batchId),
  });

export const useBrokerBatch = (brokerId: string, batchId: string) => useQuery(brokerBatchQueryOptions(brokerId, batchId));

export const brokerBatchTransactionsInfiniteQueryOptions = (brokerId: string, batchId: string) =>
  infiniteQueryOptions({
    queryKey: brokerBatchTransactionsQueryKey(brokerId, batchId),
    queryFn: ({ pageParam }) => getBrokerBatchTransactions(brokerId, batchId, pageParam),
    initialPageParam: undefined as string | undefined,
    // Cursor is OMITTED by the API when exhausted
    getNextPageParam: nextCursorParam,
    select: selectBatchTransactions,
  });

export const useBrokerBatchTransactions = (brokerId: string, batchId: string) => useInfiniteQuery(brokerBatchTransactionsInfiniteQueryOptions(brokerId, batchId));

/** Result shape passed from the page (parallel mount) into the transactions section. */
export type BrokerBatchTransactionsResult = ReturnType<typeof useBrokerBatchTransactions>;
